import type { LobbyMemberInfo } from '../../api/types';
import { teamLabel } from './seating';

/** A member's place in the lobby: a side and slot, or a bench role. */
export type Seat = { team: LobbyMemberInfo['team']; slot: number; coachTeam?: 'radiant' | 'dire' };

export type MemberMovement =
  | { kind: 'joined'; steamId: string; name: string; to: Seat }
  | { kind: 'left'; steamId: string; name: string; from: Seat }
  | { kind: 'moved'; steamId: string; name: string; from: Seat; to: Seat };

export type Movement = MemberMovement & { at: number };

export function seatOf(member: LobbyMemberInfo): Seat {
  return { team: member.team, slot: member.slot, coachTeam: member.coach_team };
}

export function seatLabel(seat: Seat): string {
  if (seat.coachTeam) return `${teamLabel(seat.coachTeam)} coach`;
  if (seat.team === 'radiant' || seat.team === 'dire') return `${teamLabel(seat.team)} ${seat.slot}`;
  return teamLabel(seat.team);
}

function sameSeat(a: Seat, b: Seat): boolean {
  return a.team === b.team && a.slot === b.slot && a.coachTeam === b.coachTeam;
}

/**
 * What moved between two readings of the lobby. Compares by Steam ID, so a
 * member who changed side, slot or coaching role reads as one move rather than
 * a leave and a join. A first reading (no previous list) reports nothing:
 * everyone being there is not a movement.
 */
export function diffMembers(
  previous: LobbyMemberInfo[] | undefined,
  next: LobbyMemberInfo[] | undefined,
): MemberMovement[] {
  if (!previous || !next) return [];

  const before = new Map(previous.map((member) => [member.steam_id, member]));
  const movements: MemberMovement[] = [];

  for (const member of next) {
    const was = before.get(member.steam_id);
    if (!was) {
      movements.push({ kind: 'joined', steamId: member.steam_id, name: member.name, to: seatOf(member) });
      continue;
    }
    before.delete(member.steam_id);
    const from = seatOf(was);
    const to = seatOf(member);
    if (!sameSeat(from, to)) {
      movements.push({ kind: 'moved', steamId: member.steam_id, name: member.name, from, to });
    }
  }

  for (const gone of before.values()) {
    movements.push({ kind: 'left', steamId: gone.steam_id, name: gone.name, from: seatOf(gone) });
  }

  return movements;
}

export function describeMovement(movement: MemberMovement): string {
  switch (movement.kind) {
    case 'joined':
      return `${movement.name} joined · ${seatLabel(movement.to)}`;
    case 'left':
      return `${movement.name} left · was ${seatLabel(movement.from)}`;
    case 'moved':
      return `${movement.name} · ${seatLabel(movement.from)} → ${seatLabel(movement.to)}`;
  }
}

/** Newest first, one entry per member, capped so the log stays readable. */
export function recordMovements(
  log: Movement[],
  movements: MemberMovement[],
  at: number,
  limit = 6,
): Movement[] {
  if (movements.length === 0) return log;
  const fresh = movements.map((movement) => ({ ...movement, at }));
  const superseded = new Set(fresh.map((movement) => movement.steamId));
  return [...fresh, ...log.filter((entry) => !superseded.has(entry.steamId))].slice(0, limit);
}

export function pruneMovements(log: Movement[], now: number, maxAgeMs: number): Movement[] {
  const kept = log.filter((movement) => now - movement.at < maxAgeMs);
  return kept.length === log.length ? log : kept;
}

/** Seconds since a movement, for a caption that reads at a glance. */
export function movementAge(movement: Movement, now: number): string {
  const seconds = Math.max(0, Math.round((now - movement.at) / 1000));
  if (seconds < 1) return 'now';
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.round(seconds / 60)}m ago`;
}
